import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  OPERATIONS_BADGE_POLL_INTERVAL_MS,
  pollingOptions,
} from 'src/constants/polling.constants';
import { useSetPageTitle } from '../../hooks/useSetPageTitle';
import { AppLayout } from '../../components/layout/app-layout';
import {
  OPERATIONS_DEFAULT_TAB,
  OPERATIONS_SIDEBAR,
  OPERATIONS_TABS,
} from '../../components/layout/operations-nav.config';
import { useGetDataJobFailuresQuery, useGetSchedulerFailuresQuery } from '../../redux/operations/operations.endpoints';
import { useGetReportingApiAlertsQuery } from '../../features/reporting-api-usage/endpoints/reporting-api-usage.endpoints';
import { getIsUserStaff } from '../../redux/user/user.selectors';
import EtlDashboardTab from './tabs/etl-dashboard.tab';
import SchedulerDashboardTab from './tabs/scheduler-dashboard.tab';
import DataTransfersTab from './tabs/data-transfers.tab';
import UsageInsightsTab from './tabs/usage-insights.tab';
import ApisTab from './tabs/apis.tab';
import SettingsTab from './tabs/settings.tab';

const OperationsPage = () => {
  useSetPageTitle('Operations');
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const isStaff = useSelector(getIsUserStaff);

  const { data: schedulerFailures } = useGetSchedulerFailuresQuery(
    undefined,
    pollingOptions(OPERATIONS_BADGE_POLL_INTERVAL_MS)
  );
  const { data: dataJobFailures } = useGetDataJobFailuresQuery(
    undefined,
    pollingOptions(OPERATIONS_BADGE_POLL_INTERVAL_MS)
  );
  const { data: reportingApiAlerts } = useGetReportingApiAlertsQuery(undefined, {
    ...pollingOptions(OPERATIONS_BADGE_POLL_INTERVAL_MS),
    skip: !isStaff,
  });
  const schedulerFailureCount = schedulerFailures?.failure_count ?? 0;
  const hasDataJobFailures = Boolean(dataJobFailures?.has_alerts);

  const visibleTabs = useMemo(
    () => OPERATIONS_TABS.filter((operationsTab) => {
      if (operationsTab.id === 'usage-insights' || operationsTab.id === 'apis') {
        return isStaff;
      }
      return true;
    }),
    [isStaff]
  );

  const visibleTabIds = useMemo(
    () => visibleTabs.map((operationsTab) => operationsTab.id),
    [visibleTabs]
  );

  const operationsTabs = useMemo(
    () =>
      visibleTabs.map((operationsTab) => {
        if (operationsTab.id === 'scheduler' && schedulerFailureCount > 0) {
          return { ...operationsTab, badge: schedulerFailureCount };
        }
        if (operationsTab.id === 'etl' && hasDataJobFailures) {
          return { ...operationsTab, badge: 1, badgeVariant: 'dot' as const };
        }
        if (operationsTab.id === 'apis' && reportingApiAlerts?.has_alerts) {
          return { ...operationsTab, badge: 1, badgeVariant: 'alert' as const };
        }
        return operationsTab;
      }),
    [hasDataJobFailures, reportingApiAlerts?.has_alerts, schedulerFailureCount, visibleTabs]
  );

  const currentTab = visibleTabIds.includes(tab ?? '')
    ? (tab as string)
    : OPERATIONS_DEFAULT_TAB;

  useEffect(() => {
    if (tab === 'data-jobs') {
      const search = window.location.search;
      navigate(`/operations/etl${search}`, { replace: true });
      return;
    }
    if (tab === 'reporting-api') {
      const search = window.location.search;
      navigate(`/operations/apis${search}`, { replace: true });
      return;
    }
    if (!tab || !visibleTabIds.includes(tab)) {
      navigate(`/operations/${OPERATIONS_DEFAULT_TAB}`, { replace: true });
    }
  }, [navigate, tab, visibleTabIds]);

  const handleTabChange = (tabId: string) => {
    navigate(`/operations/${tabId}`);
  };

  return (
    <AppLayout
      activePage={OPERATIONS_SIDEBAR.path}
      navbarProps={{
        tabs: operationsTabs,
        activeTab: currentTab,
        onTabChange: handleTabChange,
      }}
    >
      <div className="h-[calc(100vh-8rem)]">
        {currentTab === 'etl' && <EtlDashboardTab />}
        {currentTab === 'scheduler' && <SchedulerDashboardTab />}
        {currentTab === 'data-transfers' && <DataTransfersTab />}
        {currentTab === 'settings' && <SettingsTab />}
        {currentTab === 'apis' && isStaff && <ApisTab />}
        {currentTab === 'usage-insights' && isStaff && <UsageInsightsTab />}
      </div>
    </AppLayout>
  );
};

export default OperationsPage;
